<template>
  <div class="service-pricing">
    <fieldset
      v-for="group in groups"
      :key="group.key"
      class="pricing-group"
      :class="`pricing-group--${group.key}`"
    >
      <legend>{{ group.label }}</legend>
      <div class="pricing-fields">
        <div v-for="field in group.fields" :key="field.key" class="pricing-field">
          <label :for="field.key">{{ field.label }}</label>
          <div class="price-input" :class="{ 'price-input--disabled': disabled }">
            <span class="price-prefix" aria-hidden="true">{{ field.prefix }}</span>
            <input
              :id="field.key"
              type="number"
              step="0.01"
              :placeholder="field.placeholder"
              :required="field.required"
              :disabled="disabled"
              :readonly="field.readonly"
              :value="modelValue[field.key]"
              :aria-label="`${field.label}, pounds per hour`"
              @input="$emit('update-field', field.key, $event.target.value)"
            />
            <span class="price-unit" aria-hidden="true">/ hour</span>
          </div>
        </div>
      </div>
    </fieldset>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  fields: { type: Array, required: true },
  modelValue: { type: Object, required: true },
  disabled: { type: Boolean, default: false },
});

defineEmits(['update-field']);

const groups = computed(() => [
  { key: 'client', label: 'Client pricing', fields: props.fields.filter((field) => field.key === 'rate') },
  { key: 'team', label: 'Team pay', fields: props.fields.filter((field) => field.key !== 'rate') },
].filter((group) => group.fields.length));
</script>

<style scoped>
.service-pricing {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 24px;
  margin-top: 24px;
}

.pricing-group {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.pricing-group legend {
  padding: 0 0 12px;
  color: var(--text-color);
  font: 600 14px/20px var(--font-family-base);
}

.pricing-fields {
  border: 1px solid var(--border-color);
  border-bottom: 0;
}

.pricing-field {
  display: grid;
  grid-template-columns: minmax(0, 38%) minmax(0, 1fr);
  min-width: 0;
  min-height: 44px;
  border-bottom: 1px solid var(--border-color);
}

.pricing-field:not(:last-child) {
  border-image: linear-gradient(to right, transparent 16px, var(--border-color) 16px) 1;
}

.pricing-field label {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  border-right: 1px solid var(--border-color);
  background: var(--noc-background-secondary, #fafafb);
  font: 400 14px/20px var(--font-family-base);
  color: var(--text-color-secondary);
  overflow-wrap: anywhere;
}

.price-input {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 44px;
  box-sizing: border-box;
  background: var(--noc-background);
}

.price-input:not(.price-input--disabled):hover {
  background: var(--noc-background-secondary);
}

.price-input:not(.price-input--disabled):focus-within {
  background: var(--noc-background);
}

.price-input--disabled {
  opacity: 0.6;
}

.price-input input {
  flex: 1;
  width: 100%;
  min-width: 0;
  align-self: stretch;
  min-height: 44px;
  padding: 10px 8px;
  box-sizing: border-box;
  border: 0;
  border-radius: 0;
  outline: 0;
  background: transparent;
  color: var(--text-color);
  font: 400 14px/20px var(--font-family-base);
  cursor: text;
  transition: none;
  appearance: textfield;
  -moz-appearance: textfield;
}

.price-input input:focus {
  /* Match the other catalog cells, including the global input:focus override. */
  border: 0 !important;
  outline: none;
  box-shadow: none;
  background: transparent;
}

.price-input input::-webkit-inner-spin-button,
.price-input input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.price-prefix,
.price-unit {
  flex-shrink: 0;
  color: var(--text-color-secondary);
  font-size: 0.85rem;
}

.price-prefix {
  padding-left: 12px;
}

.price-unit {
  padding-right: 12px;
}

</style>
